import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Typography, Space, Divider, Progress, Spin, Descriptions, Tag, Alert } from "antd";
import { useRequest } from "../../hooks/useRequest";
import { REQUEST_UPDATE_INTERVAL } from "../../utils/constants";
import StatusTag from "../common/StatusTag";
import JsonViewer from "../common/JsonViewer";

const { Title, Text } = Typography;

/**
 * Props do componente RequestViewer.
 */
interface RequestViewerProps {
  requestId: string;
}

/**
 * Componente para visualizar uma requisição individual.
 *
 * Exibe o status, dados e informações da requisição.
 * Atualiza automaticamente quando o status está pendente ou carregando.
 */
const RequestViewer: React.FC<RequestViewerProps> = ({ requestId }) => {
  const { getRequest } = useRequest();
  const [requestValue, setRequestValue] = useState<any>(null);
  const [countTime, setCountTime] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>("loading");

  /**
   * Atualiza os dados da requisição.
   */
  const updateRequest = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRequest(requestId);
      // O resultado tem a estrutura: { requested, data, type, info } ou { response: { type, data }, requested, ... }
      setRequestValue(result);
      // O status vem de requested.status (que pode ser 'success', 'warning', 'error', 'pending')
      const newStatus = result.requested?.status || "loading";
      setStatus(newStatus);

      // Debug temporário para verificar estrutura dos dados
      if (newStatus === "warning" || newStatus === "error") {
        console.log("RequestViewer - Estrutura dos dados:", {
          status: newStatus,
          response: result.response,
          data: result.data,
          responseData: result.response?.data,
          fullResult: result,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar requisição:", error);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  }, [requestId, getRequest]);

  /**
   * Efeito para atualizar requisição inicialmente.
   */
  useEffect(() => {
    updateRequest();
  }, [updateRequest]);

  /**
   * Efeito para atualização periódica quando status está pendente.
   */
  useEffect(() => {
    if (status !== "pending" && status !== "loading") {
      return;
    }

    const interval = setInterval(() => {
      updateRequest();
    }, REQUEST_UPDATE_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [status, updateRequest]);

  /**
   * Efeito para contador regressivo.
   */
  useEffect(() => {
    if (!loading && (status === "pending" || status === "loading")) {
      if (countTime <= 0) {
        setCountTime(10);
        return;
      }

      const timeout = setTimeout(() => {
        setCountTime((prev) => {
          if (prev <= 0) {
            return 10;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearTimeout(timeout);
    } else {
      // Reset contador quando não está pendente
      setCountTime(10);
    }
  }, [countTime, loading, status]);

  const isPending = status === "pending" || status === "loading";
  const progressPercent = isPending ? ((10 - countTime) / 10) * 100 : 100;

  /**
   * Função recursiva para buscar steps com arquivos na estrutura de dados.
   * Busca em data, data.info, e outras estruturas possíveis.
   */
  const findStepsWithFiles = useCallback((obj: any, path: string = ""): any[] => {
    if (!obj || typeof obj !== "object") {
      return [];
    }

    const stepsWithFiles: any[] = [];

    // Se é um array, itera sobre os itens
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        stepsWithFiles.push(...findStepsWithFiles(obj[i], `${path}[${i}]`));
      }
      return stepsWithFiles;
    }

    // Se o objeto tem uma propriedade 'steps' que é um array
    if (obj.steps && Array.isArray(obj.steps)) {
      for (let i = 0; i < obj.steps.length; i++) {
        const step = obj.steps[i];
        // Verifica se o step tem arquivos
        if (step.files && Array.isArray(step.files) && step.files.length > 0) {
          // Retorna o step completo (todo o andamento)
          stepsWithFiles.push({
            ...step,
            _path: `${path}.steps[${i}]`,
          });
        }
      }
    }

    // Continua buscando recursivamente em outras propriedades
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && key !== "steps") {
        const newPath = path ? `${path}.${key}` : key;
        stepsWithFiles.push(...findStepsWithFiles(obj[key], newPath));
      }
    }

    return stepsWithFiles;
  }, []);

  /**
   * Memoiza os steps com arquivos para evitar recalcular a cada render
   */
  const stepsWithFiles = useMemo(() => {
    if (!requestValue || status === "pending" || status === "loading") {
      return [];
    }

    // Busca steps com arquivos em data e data.info
    const data = requestValue.data || requestValue.response?.data;
    if (!data) {
      return [];
    }

    return findStepsWithFiles(data);
  }, [requestValue, status, findStepsWithFiles]);

  return (
    <Card
      style={{
        marginBottom: 16,
        border: `1px solid ${isPending ? "#4DAB4B" : "#d9d9d9"}`,
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Text strong>Request ID: </Text>
            <Text copyable code>
              {requestId}
            </Text>
          </div>
          <StatusTag status={status} />
        </div>

        <Divider style={{ margin: "12px 0" }} />

        {isPending && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <Text type="secondary">
                {status === "pending" || status === "loading" ? (
                  <>
                    Reiniciando em: <Text strong>{countTime}s</Text>
                  </>
                ) : (
                  "Requisição finalizada!!"
                )}
              </Text>
            </div>
            <Progress percent={progressPercent} status="active" strokeColor="#4DAB4B" showInfo={false} />
          </div>
        )}

        {!isPending && status !== "loading" && requestValue && (
          <>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Status">
                <StatusTag status={status} />
              </Descriptions.Item>
            </Descriptions>

            {/* Campos principais em linha para economizar espaço */}
            <div style={{ marginTop: 16 }}>
              <Space wrap size="small">
                {requestValue.requested?.source && <Tag color="blue">{requestValue.requested.source}</Tag>}
                {requestValue.requested?.platform && <Tag color="cyan">{requestValue.requested.platform}</Tag>}
                {requestValue.requested?.query && <Tag color="purple">{requestValue.requested.query}</Tag>}
                {requestValue.requested?.court && <Tag color="geekblue">{requestValue.requested.court}</Tag>}
                {requestValue.requested?.search && <Tag color="volcano">{requestValue.requested.search}</Tag>}
              </Space>
            </div>

            {/* Mostra data apenas quando o status for warning ou error, abaixo dos campos */}
            {(status === "warning" || status === "error") &&
              (() => {
                // Busca o data em response.data (conforme informado pelo usuário)
                // Também verifica no nível raiz como fallback
                const dataValue = requestValue.response?.data || requestValue.data;

                // Se não encontrou data, não exibe nada
                if (!dataValue) {
                  return null;
                }

                return <div style={{ marginTop: 12 }}>{typeof dataValue === "string" ? <Alert message={<Text copyable>{dataValue}</Text>} type={status === "warning" ? "warning" : "error"} showIcon /> : <Alert message={<Text copyable>{JSON.stringify(dataValue, null, 2)}</Text>} type={status === "warning" ? "warning" : "error"} showIcon />}</div>;
              })()}
          </>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
          </div>
        )}

        {requestValue && (
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Title level={5}>Resposta Completa:</Title>
            <JsonViewer src={requestValue} name="response" collapsed={true} />
          </div>
        )}

        {/* Seção de Andamentos com Arquivos */}
        {requestValue && !loading && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Title level={5} style={{ margin: 0 }}>
                Andamentos com Arquivos:
              </Title>
              {stepsWithFiles.length === 0 && (
                <Text type="secondary" style={{ fontSize: 14 }}>
                  (Não há arquivos)
                </Text>
              )}
            </div>
            {stepsWithFiles.length > 0 && (
              <div style={{ marginTop: 12, width: '100%', overflowX: 'auto' }}>
                <JsonViewer src={stepsWithFiles} name="andamentos-com-arquivos" collapsed={false} />
              </div>
            )}
          </div>
        )}
      </Space>
    </Card>
  );
};

export default RequestViewer;
